/**
 * Format an ISO timestamp as a relative time ("2 minutes ago", "3 days ago").
 * Uses the browser's built-in Intl.RelativeTimeFormat — no dependency, locale-aware.
 *
 * Returns an absolute date string for anything older than ~60 days, because
 * "42 days ago" stops being more useful than "Feb 12" past a certain point.
 */
const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto', style: 'short' })

const MINUTE = 60
const HOUR   = 60 * MINUTE
const DAY    = 24 * HOUR
const WEEK   = 7 * DAY
const MONTH  = 30 * DAY

export function timeAgo(iso?: string | null): string {
  if (!iso) return ''
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return ''
  const deltaSec = Math.round((then - Date.now()) / 1000) // negative = past
  const abs = Math.abs(deltaSec)

  if (abs < MINUTE)       return rtf.format(Math.round(deltaSec), 'second')
  if (abs < HOUR)         return rtf.format(Math.round(deltaSec / MINUTE), 'minute')
  if (abs < DAY)          return rtf.format(Math.round(deltaSec / HOUR), 'hour')
  if (abs < WEEK)         return rtf.format(Math.round(deltaSec / DAY), 'day')
  if (abs < MONTH)        return rtf.format(Math.round(deltaSec / WEEK), 'week')
  if (abs < 60 * DAY)     return rtf.format(Math.round(deltaSec / MONTH), 'month')
  // Past ~2 months, drop to an absolute short date — "3 months ago" is less
  // scannable than "Feb 12" at that distance.
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}
